/**
 * Day/night lighting.
 *
 * The clock maths mirrors backend/src/game/clock/clock.math.ts. It is duplicated
 * rather than shared because the client renders lighting every frame and cannot
 * poll for it — but the epoch and phase table are fetched from the server, so the
 * numbers live in one place and only the arithmetic is repeated. The client also
 * stores the server's clock offset, so a skewed system clock lies only to your own
 * lighting; anything that matters (growth, spawn budget) is decided server-side.
 *
 * Rendering: one full-viewport radial-gradient sprite, centred on the hero,
 * transparent out to the visibility radius and opaque beyond. That is a single
 * sprite rather than a dark rect plus an erase-blended hole — 'erase' needs the
 * container to own a render target, and four rects around a gradient is fiddly
 * geometry that breaks at the screen edges. The gradient is baked to a canvas and
 * only rebuilt when the viewport or the radius actually changes.
 */
import { Container, Sprite, Texture } from 'pixi.js';
import { TILE } from './world';

export type Phase = 'day' | 'dusk' | 'night' | 'dawn';

export interface ClockConfig {
  epochMs: number;
  dayMs: number;
  phases: { name: Phase; ms: number }[];
  /** serverNow - Date.now() at the moment of fetch. Added to every local read. */
  offsetMs: number;
}

export interface WorldTime {
  dayNumber: number;
  phase: Phase;
  dayProgress: number;
  isDark: boolean;
}

export function worldTimeAt(cfg: ClockConfig, localNow: number = Date.now()): WorldTime {
  const since = localNow + cfg.offsetMs - cfg.epochMs;
  const t = ((since % cfg.dayMs) + cfg.dayMs) % cfg.dayMs;
  let acc = 0;
  let phase: Phase = 'day';
  for (const p of cfg.phases) {
    if (t < acc + p.ms) { phase = p.name; break; }
    acc += p.ms;
  }
  return {
    dayNumber: Math.floor(since / cfg.dayMs),
    phase,
    dayProgress: t / cfg.dayMs,
    isDark: phase !== 'day',
  };
}

/**
 * §5: darkness is a single flat dim — NO coloured sunset/dawn phases, no per-region
 * sky gradient. One dark overlay colour whose ALPHA fades between day (≈0) and
 * night over the short dusk/dawn windows. The radial torch/visibility light is
 * still applied on top (see `update` radius) so night sight mechanics work.
 */
// Owner request: it is ALWAYS day — never paint the darkness overlay. The server
// clock/phase still advances (spawn budgets, growth), but the screen stays lit.
export const ALWAYS_DAY = true;

const DARK_TINT = 0x0a1424;     // one cool-dark overlay colour, all phases
const DIM: Record<Phase, number> = {   // overlay alpha per phase (day barely there)
  day: 0.05, dusk: 0.5, night: 0.82, dawn: 0.5,
};

const VIS_TILES = { dark: 8, torch: 14 } as const;

/** Plain alpha fade between adjacent phases — the only thing that animates now. */
function blend(cfg: ClockConfig, now: number): number {
  const dayMs = cfg.dayMs;
  const since = now + cfg.offsetMs - cfg.epochMs;
  const t = ((since % dayMs) + dayMs) % dayMs;

  let acc = 0, idx = 0;
  for (let i = 0; i < cfg.phases.length; i++) {
    if (t < acc + cfg.phases[i].ms) { idx = i; break; }
    acc += cfg.phases[i].ms;
  }
  const p = cfg.phases[idx];
  const into = (t - acc) / p.ms;
  const next = cfg.phases[(idx + 1) % cfg.phases.length];
  const a = DIM[p.name], b = DIM[next.name];
  // Cross-fade only over the last quarter of a phase (short fade, plain dim).
  const k = into < 0.75 ? 0 : (into - 0.75) / 0.25;
  return a + (b - a) * k;
}

/** Bake the night hole: transparent within `holePx`, a soft ramp, then fully
 * opaque out to the edge. The canvas is sized to blanket the whole screen no
 * matter where on screen the hero stands, so the dark never "pulls back" from an
 * edge as he moves — only the lit circle follows him. The ramp is tied to the
 * hole (not the canvas), so a bigger canvas doesn't loosen the circle. */
function gradientTexture(size: number, holePx: number): Texture {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const r = size / 2;
  const inner = Math.min(holePx, r * 0.95);
  const outer = Math.min(r, inner + Math.max(56, inner * 0.6));
  const grad = g.createRadialGradient(r, r, inner, r, r, outer);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(1, 'rgba(255,255,255,1)');
  g.fillStyle = grad;             // beyond `outer` the last stop (opaque) fills the rest
  g.fillRect(0, 0, size, size);
  return Texture.from(c);
}

/** Baked soft-white radial glow (for point lights). Centre bright → transparent edge. */
function glowTexture(size = 40): Texture {
  // Baked SMALL (40px) then scaled up with nearest-neighbour, so the firelight
  // reads as chunky pixel-art bloom rather than a smooth photographic halo.
  // Alphas kept low — a gentle warm pool, not a floodlight.
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const g = c.getContext('2d')!;
  const r = size / 2;
  const grad = g.createRadialGradient(r, r, 0, r, r, r);
  grad.addColorStop(0, 'rgba(255,236,188,0.5)');    // warm firelight core (dimmed)
  grad.addColorStop(0.5, 'rgba(255,210,146,0.22)');
  grad.addColorStop(1, 'rgba(255,198,128,0)');
  g.fillStyle = grad;
  g.fillRect(0, 0, size, size);
  const tex = Texture.from(c);
  tex.source.scaleMode = 'nearest';   // pixelate on upscale
  return tex;
}

export interface DayNight {
  layer: Container;
  /** Call per frame with the hero's SCREEN position. */
  update(cfg: ClockConfig | null, heroScreenX: number, heroScreenY: number, torch: boolean, scale?: number, now?: number): void;
  /** Additive warm glows at light sources (fire/torch/lantern), SCREEN coords + radius px.
   *  `alpha` scales the whole set (a faint daytime shimmer → full at night). */
  paintLights(sources: { x: number; y: number; r: number }[], alpha: number): void;
  resize(w: number, h: number): void;
  destroy(): void;
}

/**
 * Light pools are NOT gated on the darkness sheet being drawn.
 *
 * With `ALWAYS_DAY` on, the dark overlay never paints — and when the glow layer
 * was hidden alongside it, a lit torch threw no light at all (owner: "it's
 * connected without the shine"). The two are now independent: the dark sheet
 * comes and goes with the clock, while a burning torch always casts at least
 * this much warm additive light so you can see it is alight in broad daylight.
 */
const DAY_LIGHT_FLOOR = 0.35;

export function createDayNight(viewW: number, viewH: number): DayNight {
  const layer = new Container();
  layer.eventMode = 'none'; // never eat a click meant for the world
  // The container itself is always visible; the DARK SHEET below and each glow
  // sprite decide for themselves whether they draw.
  const sprite = new Sprite();
  sprite.anchor.set(0.5);
  layer.addChild(sprite);
  // Point-light glows drawn ON TOP of the dark sheet (additive), so a torch/fire/
  // lantern lights a warm circle into the night. A reused sprite pool keeps it cheap.
  const glowTex = glowTexture();
  const lightLayer = new Container();
  lightLayer.eventMode = 'none';
  layer.addChild(lightLayer);
  const glowPool: Sprite[] = [];

  let w = viewW, h = viewH;
  let bakedRadius = -1;
  let tex: Texture | null = null;
  // Canvas large enough that, centred on the hero at ANY on-screen position, it
  // still covers every screen pixel (half-size ≥ the hero-to-far-corner distance).
  let coverage = Math.ceil(Math.hypot(w, h)) * 2;

  const ensure = (radiusPx: number) => {
    if (tex && bakedRadius === radiusPx) return;
    const old = tex;
    tex = gradientTexture(coverage, radiusPx);
    sprite.texture = tex;
    bakedRadius = radiusPx;
    old?.destroy(true);
  };

  return {
    layer,
    paintLights(sources, alpha) {
      // Grow the pool as needed; reuse sprites frame to frame.
      while (glowPool.length < sources.length) {
        const s = new Sprite(glowTex);
        s.anchor.set(0.5);
        s.blendMode = 'add';
        lightLayer.addChild(s);
        glowPool.push(s);
      }
      // A flame never sits still: the pool breathes on a clock (it used to key the
      // "flicker" off the source's screen POSITION, which is constant for a planted
      // torch — so it never actually flickered). Each source gets its own phase
      // offset so a row of torches doesn't pulse in lockstep.
      const eff = Math.max(alpha, DAY_LIGHT_FLOOR * (sources.length ? 1 : 0));
      const t = performance.now() / 1000;
      for (let i = 0; i < glowPool.length; i++) {
        const g = glowPool[i], src = sources[i];
        if (!src || eff <= 0.01) { g.visible = false; continue; }
        g.visible = true;
        g.position.set(src.x, src.y);
        const pulse = 1 + Math.sin(t * 6.3 + i * 1.7) * 0.06;   // ±6% breathing
        g.width = g.height = src.r * 2 * pulse;                 // glow diameter
        g.alpha = Math.min(1, eff) * (0.55 + Math.sin(t * 8.1 + i * 2.3) * 0.12);
      }
    },
    update(cfg, hx, hy, torch, scale = 1, now = Date.now()) {
      // Only the DARK SHEET is switched off here. `layer` stays visible so light
      // pools painted by paintLights still reach the screen (see DAY_LIGHT_FLOOR).
      if (ALWAYS_DAY) { sprite.visible = false; return; }
      if (!cfg) { sprite.visible = false; return; }
      const alpha = blend(cfg, now);
      if (alpha <= 0.005) { sprite.visible = false; return; }
      layer.visible = true;
      sprite.visible = true;
      const t = worldTimeAt(cfg, now);
      sprite.tint = DARK_TINT;
      sprite.alpha = alpha;
      if (t.phase !== 'night') {
        // Day / dusk / dawn: a flat, uniform dim pinned to the SCREEN (no hole,
        // no hero-centring) — so it never slides or "zooms" as the hero walks.
        sprite.texture = Texture.WHITE;
        sprite.anchor.set(0, 0);
        sprite.scale.set(1);
        sprite.position.set(0, 0);
        sprite.width = w; sprite.height = h;
        return;
      }
      // Night: a full-screen dark blanket with a lit circle punched around the
      // hero. The circle radius tracks the camera zoom so it covers a consistent
      // patch of the world; rounded to 8px so a smooth zoom doesn't thrash re-bakes.
      const radius = Math.round((torch ? VIS_TILES.torch : VIS_TILES.dark) * TILE * scale / 8) * 8;
      ensure(radius);
      sprite.anchor.set(0.5, 0.5);
      sprite.scale.set(1);
      sprite.position.set(hx, hy);
    },
    resize(nw, nh) {
      w = nw; h = nh;
      coverage = Math.ceil(Math.hypot(w, h)) * 2;
      bakedRadius = -1; // force a re-bake at the new size
    },
    destroy() {
      tex?.destroy(true);
      layer.destroy({ children: true });
    },
  };
}
